import React from "react";

const Pricing = ({ onRegisterClick }) => {
  const plan = {
    name: "Plan Examen Aprobado",
    price: 14.99,
    description: "Todo el contenido y herramientas de IA incluidas.",
    features: [
      "Listado de leyes completa y actualizada",
      "Preguntas de práctica ilimitadas",
      "Simulador Real de examen (100 preguntas)",
      "Explicaciones detalladas 24/7",
      "Análisis de progreso detallado",
      "Soporte prioritario",
    ],
  };

  return (
    <section
      id="pricing"
      className="relative py-20 bg-slate-50 overflow-hidden"
    >
      {/* ELEMENTOS DE FONDO PROFESIONAL */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        {/* Patrón de puntos sutil */}
        <div
          className="absolute inset-0 opacity-[0.15]"
          style={{
            backgroundImage: `radial-gradient(#0f172a 1px, transparent 1px)`,
            backgroundSize: "32px 32px",
          }}
        ></div>

        {/* Luces de gradiente desenfocadas (Mesh) */}
        <div className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] rounded-full bg-blue-100 blur-[120px] opacity-60"></div>
        <div className="absolute -bottom-[10%] -right-[10%] w-[40%] h-[40%] rounded-full bg-indigo-50 blur-[120px] opacity-60"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header simplificado */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-6">
            Un solo precio <br />
            <span className="text-blue-600">Acceso total</span>
          </h2>
          <p className="text-xl text-slate-600 font-medium">
            Sin planes confusos, sin sorpresas. Acceso completo a todos los
            recursos.
          </p>
        </div>

        {/* Tarjeta de Precio Único */}
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-[2.5rem] border-2 border-slate-900 shadow-[10px_10px_0px_0px_rgba(15,23,42,1)] p-8 md:p-10 hover:translate-x-1 hover:-translate-y-1 transition-all">
            <div className="mb-8">
              <h3 className="text-2xl font-black text-slate-900 mb-2">
                {plan.name}
              </h3>
              <p className="text-slate-500 text-sm font-medium">
                {plan.description}
              </p>
            </div>

            <div className="mb-10">
              <div className="flex items-baseline gap-1">
                <span className="text-7xl font-black text-slate-900">
                  ${plan.price}
                </span>
                <span className="text-xl text-slate-400 font-bold">/mes</span>
              </div>
              <p className="text-sm font-bold text-blue-600 mt-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></span>
                Cancela cuando quieras, sin compromiso
              </p>
            </div>

            {/* Lista de Features */}
            <div className="space-y-4 mb-10">
              {plan.features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <svg
                    className="w-6 h-6 text-emerald-500 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="3"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-slate-700 font-bold text-sm">
                    {feature}
                  </span>
                </div>
              ))}
            </div>

            {/* Botón de Acción Principal */}
            <button
              onClick={onRegisterClick}
              className="w-full py-5 rounded-2xl font-black text-xl bg-slate-900 text-white hover:bg-blue-600 shadow-xl transition-all active:scale-95"
            >
              Suscribirme Ahora
            </button>

            <div className="mt-8 flex flex-col items-center gap-4">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="w-8 h-8 rounded-full border-2 border-white bg-slate-200 flex items-center justify-center text-[10px] font-bold"
                  >
                    👤
                  </div>
                ))}
                <div className="w-8 h-8 rounded-full border-2 border-white bg-blue-100 flex items-center justify-center text-[10px] font-bold text-blue-600">
                  +200
                </div>
              </div>
              <p className="text-[11px] text-slate-400 font-bold uppercase tracking-widest">
                Únete a cientos de aspirantes
              </p>
            </div>
          </div>
        </div>

        {/* Footer de confianza */}
        <div className="relative z-10 mt-12 text-center flex flex-col items-center gap-6">
          {/* Logos de Métodos de Pago */}
          <div className="flex items-center justify-center gap-8 opacity-40 grayscale hover:grayscale-0 hover:opacity-100 transition-all duration-500">
            <img
              src="https://cdn.simpleicons.org/visa/slate-900"
              alt="Visa"
              className="h-6 w-auto"
            />
            <img
              src="https://cdn.simpleicons.org/mastercard/slate-900"
              alt="Mastercard"
              className="h-8 w-auto"
            />
          </div>

          <div className="flex flex-col items-center gap-2">
            <div className="flex items-center gap-2 text-emerald-600">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-xs font-bold uppercase tracking-widest">
                Pago 100% Seguro
              </span>
            </div>
            <p className="text-slate-400 text-[10px] font-medium max-w-xs leading-relaxed">
              Tus datos están protegidos por encriptación SSL de 256 bits. No
              almacenamos los datos de tu tarjeta.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
